import type { ConversationPersistence } from '@/services/types'
import { isEmbeddedWindow } from '@/utils/windowContext'

export type ChatSurface =
  | 'desktop_workspace'
  | 'helper_embed'
  | 'canvas_workspace'
  | 'canvas_bot'
  | 'canvas_assignment'
export type LaunchContext = 'cas' | 'lti_study' | 'lti_assignment'
export type SessionScope = 'user_all' | 'bot_only' | 'fixed_session'
export type BotCatalogScope = 'all_allowed' | 'academic_only' | 'single_bot'
export type UiPolicyPreset =
  | 'desktop_standard'
  | 'embedded_helper'
  | 'canvas_workspace'
  | 'canvas_student'
  | 'assignment_locked'

export interface SurfaceExperienceConfig {
  conversationMode?: ConversationPersistence
  sessionScope?: SessionScope
  botCatalogScope?: BotCatalogScope
  uiPolicy?: UiPolicyPreset
}

export interface AppConfig {
  apiBaseUrl: string
  casBaseUrl?: string
  casLoginUrl?: string
  casLogoutUrl?: string
  defaultAnonymousConversationMode?: ConversationPersistence
  surfaces?: Partial<Record<ChatSurface, SurfaceExperienceConfig>>
}

export interface RuntimeContext {
  surface: ChatSurface
  launchContext: LaunchContext
  isEmbedded: boolean
}

let configCache: AppConfig | null = null
const DEFAULT_CONFIG_FILE = 'config.json'

function getConfigFileName(): string {
  const configured = import.meta.env.VITE_APP_CONFIG_NAME
  return typeof configured === 'string' && configured.trim()
    ? configured.trim()
    : DEFAULT_CONFIG_FILE
}

export async function loadConfig(): Promise<AppConfig> {
  if (configCache) return configCache
  
  const response = await fetch(`/${getConfigFileName()}`)
  configCache = await response.json()
  return configCache!
}

// Sync access after config is loaded
export function getConfig(): AppConfig {
  if (!configCache) throw new Error('Config not loaded. Call loadConfig() first.')
  return configCache
}

function isEmbeddedContext(): boolean {
  return isEmbeddedWindow()
}

export function getRuntimeContext(
  overrides: Partial<Pick<RuntimeContext, 'surface' | 'launchContext'>> = {},
): RuntimeContext {
  const normalizedSurface = overrides.surface ?? (isEmbeddedContext() ? 'helper_embed' : 'desktop_workspace')
  const launchContext = overrides.launchContext ?? 'cas'
  return {
    surface: normalizedSurface,
    launchContext,
    isEmbedded: normalizedSurface !== 'desktop_workspace',
  }
}

export function getSurfaceExperienceConfig(surface: ChatSurface): SurfaceExperienceConfig {
  const config = getConfig()
  return config.surfaces?.[surface] ?? {}
}

export function getDefaultConversationMode(
  surface: ChatSurface,
  authState: { isAuthenticated: boolean },
): ConversationPersistence {
  const config = getConfig()
  if (!authState.isAuthenticated) {
    return config.defaultAnonymousConversationMode ?? 'ephemeral'
  }
  return getSurfaceExperienceConfig(surface).conversationMode
    ?? (surface === 'helper_embed' ? 'ephemeral' : 'persistent')
}
